import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getFirstExistingKey } from 'utils/i18nUtils'
import { StepOption } from './types'
import { TextDiscount } from '@ca/common/types'
import { resolvePoints } from './helpers'

interface Args {
  lookup: StepOption['lookup']
}

export default function useStepOptionTranslation({ lookup }: Args) {
  const { i18n, t } = useTranslation(['steps', 'attributes'])

  const points = useMemo(() => {
    const resolved = resolvePoints(i18n, lookup || [])

    return resolved
  }, [lookup, i18n])

  const discountAmount = useMemo(() => {
    if (!lookup) {
      return undefined
    }
    const key = getFirstExistingKey(
      i18n,
      lookup.map((l) => `${l}.discount.amount`)
    )
    return key ? t(key) : undefined
  }, [lookup, i18n, t])

  const discountLabel = useMemo(() => {
    if (!lookup) {
      return undefined
    }
    const key = getFirstExistingKey(
      i18n,
      lookup.map((l) => `${l}.discount.label`)
    )
    return key ? t(key) : undefined
  }, [lookup, i18n, t])

  const discount = useMemo(() => {
    if (!discountAmount) {
      return undefined
    }
    return {
      scope: 'text',
      text: discountAmount,
      label: discountLabel,
    } as TextDiscount
  }, [discountAmount, discountLabel])

  const imageText = useMemo(() => {
    const key = getFirstExistingKey(
      i18n,
      lookup?.map((l) => `${l}.imageText`) || []
    )
    return key ? t(key) : undefined
  }, [lookup, i18n, t])

  const recommendedText = useMemo(() => {
    const key = getFirstExistingKey(
      i18n,
      lookup?.map((l) => `${l}.recommendedText`) || []
    )
    return key ? t(key) : undefined
  }, [lookup, i18n, t])

  return {
    points,
    discount,
    imageText,
    recommendedText,
  }
}
